/**
 * AnimatedStat — a counter that counts up from 0 to a target value
 * with a smooth ease-out animation, triggered by IntersectionObserver.
 */
export class AnimatedStat {
  /**
   * Create an animated counter element.
   * @param {Object} options
   * @param {number} options.value - Target numeric value
   * @param {string} options.label - Label text (e.g., "Total Races")
   * @param {string} [options.suffix=''] - Unit suffix (e.g., "km", "races")
   * @param {number} [options.duration=1500] - Animation duration in ms
   * @param {number} [options.decimals=0] - Decimal places
   * @returns {HTMLElement}
   */
  static create({ value, label, suffix = '', duration = 1500, decimals = 0, formatValue = null }) {
    const el = document.createElement('div');
    el.className = 'animated-stat';

    const valueEl = document.createElement('span');
    valueEl.className = 'animated-stat__value';
    valueEl.textContent = '0';

    const suffixEl = document.createElement('span');
    suffixEl.className = 'animated-stat__suffix';
    suffixEl.textContent = suffix ? ` ${suffix}` : '';

    const labelEl = document.createElement('span');
    labelEl.className = 'animated-stat__label';
    labelEl.textContent = label;

    const valueRow = document.createElement('div');
    valueRow.className = 'animated-stat__value-row';
    valueRow.appendChild(valueEl);
    valueRow.appendChild(suffixEl);

    el.appendChild(valueRow);
    el.appendChild(labelEl);

    // Use IntersectionObserver to start animation when visible
    let hasAnimated = false;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            hasAnimated = true;
            observer.disconnect();
            if (formatValue) {
              // Static display (e.g., Marathon PB "4:17")
              valueEl.textContent = formatValue(value);
            } else {
              AnimatedStat._animate(valueEl, value, duration, decimals);
            }
          }
        });
      },
      { threshold: 0.3 }
    );

    // Observe once appended to DOM — defer to next microtask
    requestAnimationFrame(() => {
      observer.observe(el);
    });

    return el;
  }

  /**
   * Animate from 0 to target using requestAnimationFrame with ease-out.
   * @param {HTMLElement} el - Element to update textContent on
   * @param {number} target - Target value
   * @param {number} duration - Duration in ms
   * @param {number} decimals - Decimal places
   */
  static _animate(el, target, duration, decimals) {
    const start = performance.now();

    function tick(now) {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);

      // Ease-out: 1 - (1 - t)^3
      const eased = 1 - Math.pow(1 - progress, 3);

      const current = eased * target;
      el.textContent = current.toLocaleString('en-IN', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });

      if (progress < 1) {
        requestAnimationFrame(tick);
      }
    }

    requestAnimationFrame(tick);
  }
}
